import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Easing,
  Audio,
  staticFile,
  Sequence,
  Img,
} from "remotion";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {AccentLine} from "../components/dimus/AccentLine";
import {KineticText} from "../components/text/KineticText";
import {SceneBackground} from "../components/backgrounds/SceneBackground";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {OrganicLayer} from "../components/overlays/OrganicLayer";

/**
 * CAFÉ COM TEU ROI EP13 — "O processo que ninguém vê é o que sustenta o resultado"
 * Editing Style: DYNAMIC
 * Timings: real audio durations + 25-frame dynamic transitions
 */

// Durações reais ffprobe (frames @ 30fps)
const SCENES = [
  {dur: 85,  file: "audio/cafe-ep13/cafe_ep13_c01.mp3", bg: "floating-shapes" as const},   // 2.8s
  {dur: 181, file: "audio/cafe-ep13/cafe_ep13_c02.mp3", bg: "grid-dots" as const},       // 6.0s
  {dur: 407, file: "audio/cafe-ep13/cafe_ep13_c03.mp3", bg: "gradient-drift" as const},  // 13.6s
  {dur: 199, file: "audio/cafe-ep13/cafe_ep13_c04.mp3", bg: "dark-void" as const},      // 6.6s
  {dur: 220, file: "audio/cafe-ep13/cafe_ep13_c05.mp3", bg: "warm-glow" as const},       // 7.3s
];

// Transition: 25 frames each (≈0.83s) for 4 transitions
const TRANSITION_DUR = 25;
export const CAFE_EP13_TOTAL = SCENES.reduce((s, sc) => s + sc.dur, 0) + SCENES.length * TRANSITION_DUR;

// Total: 85+181+407+199+220 + 125 = 1217 frames (~40.5s)
const YELLOW = IDV.third;
const GREEN = IDV.secondary;
const BLUE = IDV.primary;
const RED = IDV.error;
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a7";
const SZ = SAFE_ZONES.universal;

const BGM_VOLUME = 0.18;

// ─── SceneFade with dynamic overlap ─────────────────────────────────
/**
 * Fade in first 12 frames, fade out last 15 frames.
 * Creates smooth transition INTO and OUT OF scene.
 */
const SceneFade: React.FC<{duration: number; children: React.ReactNode}> = ({duration, children}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 12], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 15, duration], [1, 0], CLAMP);
  return <AbsoluteFill style={{opacity: Math.min(fadeIn, fadeOut)}}>{children}</AbsoluteFill>;
};

// ─── Pill badge ──────────────────────────────────────────────────────
const Pill: React.FC<{text: string; color: string; delay: number}> = ({text, color, delay}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p,
      transform: `scale(${interpolate(p, [0, 1], [0.7, 1], CLAMP)})`,
      padding: "10px 20px", borderRadius: 999,
      background: `${color}18`, border: `1px solid ${color}44`,
    }}>
      <span style={{fontSize: FONT_SCALE.label.min, fontWeight: 700, color, fontFamily: FONT_STACK}}>{text}</span>
    </div>
  );
};

// ─── SCENE 1: ABERTURA ───────────────────────────────────────────────
const S1: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const titleP = spring({frame: frame - 8, fps, config: SPRINGS.bouncy});
  const lineP = spring({frame: frame - 35, fps, config: SPRINGS.snappy});
  const subtitleP = spring({frame: frame - 50, fps, config: SPRINGS.smooth});

  return (
    <OrganicLayer intensity="subtle" seed="cafe-ep13">
    <AbsoluteFill>
      <SceneBackground mode={SCENES[0].bg} />
      <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 16}}>
        <Audio src={staticFile(SCENES[0].file)} volume={0.9} />
        <Pill text="☕ EP13" color={YELLOW} delay={5} />
        <div style={{
          opacity: titleP,
          transform: `translateY(${interpolate(titleP, [0, 1], [25, 0], CLAMP)}px)`,
          fontSize: FONT_SCALE.h1.min,
          fontWeight: 800,
          color: YELLOW,
          fontFamily: FONT_STACK,
          letterSpacing: -1,
        }}>
          CAFÉ COM TEU ROI
        </div>
        <div style={{
          opacity: lineP,
          transform: `scaleX(${lineP})`,
          transformOrigin: "left",
          width: 200, height: 3,
          background: YELLOW,
          borderRadius: 2,
          marginTop: 4,
        }} />
        <div style={{
          opacity: subtitleP,
          fontSize: FONT_SCALE.body.max,
          color: MUTED,
          fontFamily: FONT_STACK,
          marginTop: 12,
        }}>
          Tema: o processo invisível
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
    </OrganicLayer>
  );
};

// ─── SCENE 2: PRINCÍPIO ──────────────────────────────────────────────
const S2: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 10, fps, config: SPRINGS.heavy});
  const p2 = spring({frame: frame - 70, fps, config: SPRINGS.smooth});
  const p3 = spring({frame: frame - 130, fps, config: SPRINGS.bouncy});

  return (
    <AbsoluteFill>
      <SceneBackground mode={SCENES[1].bg} />
      <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 24}}>
        <Audio src={staticFile(SCENES[1].file)} volume={0.9} />
        <Pill text="PRINCÍPIO DO DIA" color={GREEN} delay={5} />
        <div style={{
          opacity: p1,
          transform: `translateY(${interpolate(p1, [0, 1], [20, 0], CLAMP)}px)`,
          fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE,
          fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.2, maxWidth: "90%",
        }}>
          Resultado bonito vem de processo feio.
        </div>
        <div style={{
          opacity: p2,
          fontSize: FONT_SCALE.body.max, fontWeight: 500, color: MUTED,
          fontFamily: FONT_STACK, textAlign: "center",
        }}>
          O que aparece é o faturamento.
        </div>
        <div style={{
          opacity: p3,
          fontSize: FONT_SCALE.body.max, fontWeight: 600, color: GREEN,
          fontFamily: FONT_STACK, textAlign: "center",
        }}>
          O que sustenta é o processo que ninguém vê.
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ─── SCENE 3: REFLEXÃO ───────────────────────────────────────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const bullets = [
    {text: "Todo mundo quer o resultado", delay: 15},
    {text: "Faturamento crescendo", delay: 75},
    {text: "Agenda cheia", delay: 135},
    {text: "Lead entrando", delay: 195},
    {text: "Mas ninguém quer montar a planilha", delay: 255, color: YELLOW},
    {text: "Documentar o processo", delay: 300, color: YELLOW},
    {text: "Fazer o script de atendimento", delay: 345, color: YELLOW},
  ];

  return (
    <AbsoluteFill>
      <SceneBackground mode={SCENES[2].bg} />
      <AbsoluteFill style={{justifyContent: "flex-start", alignItems: "flex-start", padding: `${SZ.top + 50}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 18}}>
        <Audio src={staticFile(SCENES[2].file)} volume={0.9} />
        <Pill text="REFLEXÃO" color={BLUE} delay={5} />

        {bullets.map((bullet, i) => {
          const bP = spring({frame: frame - bullet.delay, fps, config: SPRINGS.snappy});
          return (
            <div key={i} style={{
              opacity: bP,
              transform: `translateX(${interpolate(bP, [0, 1], [-20, 0], CLAMP)}px)`,
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <div style={{width: 6, height: 6, borderRadius: "50%", background: bullet.color || WHITE}} />
              <span style={{
                fontSize: FONT_SCALE.body.max,
                color: bullet.color || WHITE,
                fontFamily: FONT_STACK,
              }}>
                {bullet.text}
              </span>
            </div>
          );
        })}

        <div style={{
          marginTop: 24,
          padding: 16,
          borderRadius: 12,
          background: `${RED}15`,
          border: `1px solid ${RED}33`,
        }}>
          <div style={{
            fontSize: FONT_SCALE.body.max,
            color: RED,
            fontFamily: FONT_STACK,
            fontStyle: "italic",
            textAlign: "center",
          }}>
            Empresa que cresce sem processo é igual barraca de praia em dia de vento: quando o vento para, cai tudo.
          </div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ─── SCENE 4: COMPROMISSO ────────────────────────────────────────────
const S4: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 10, fps, config: SPRINGS.heavy});
  const p2 = spring({frame: frame - 100, fps, config: SPRINGS.bouncy});
  const p3 = spring({frame: frame - 150, fps, config: SPRINGS.smooth});

  return (
    <AbsoluteFill>
      <SceneBackground mode={SCENES[3].bg} />
      <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 24}}>
        <Audio src={staticFile(SCENES[3].file)} volume={0.9} />
        <Pill text="COMPROMISSO DO DONO" color={YELLOW} delay={5} />
        <div style={{
          opacity: p1,
          transform: `translateY(${interpolate(p1, [0, 1], [20, 0], CLAMP)}px)`,
          fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE,
          fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.2, maxWidth: "90%",
        }}>
          Hoje eu vou pegar uma coisa que funciona na minha empresa e documentar como ela funciona.
        </div>
        <div style={{
          opacity: p2,
          fontSize: FONT_SCALE.body.max, fontWeight: 700, color: GREEN,
          fontFamily: FONT_STACK,
        }}>
          Passo a passo.
        </div>
        <div style={{
          opacity: p3,
          fontSize: FONT_SCALE.body.max, color: MUTED,
          fontFamily: FONT_STACK,
        }}>
          Pra não depender só de mim.
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ─── SCENE 5: DESAFIO + CTA ──────────────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const cta1P = spring({frame: frame - 8, fps, config: SPRINGS.heavy});
  const cta2P = spring({frame: frame - 80, fps, config: SPRINGS.bouncy});
  const docP = spring({frame: frame - 120, fps, config: SPRINGS.snappy});
  const profileP = spring({frame: frame - 160, fps, config: SPRINGS.snappy});
  const closeP = spring({frame: frame - 190, fps, config: SPRINGS.smooth});

  return (
    <AbsoluteFill>
      <SceneBackground mode={SCENES[4].bg} />
      <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
        <Audio src={staticFile(SCENES[4].file)} volume={0.9} />
        <Pill text="DESAFIO" color={GREEN} delay={5} />

        <div style={{
          opacity: cta1P,
          transform: `translateY(${interpolate(cta1P, [0, 1], [20, 0], CLAMP)}px)`,
          fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE,
          fontFamily: FONT_STACK, textAlign: "center", maxWidth: "90%",
        }}>
          Pega o processo mais importante da tua operação.
        </div>

        <div style={{
          opacity: cta2P,
          fontSize: FONT_SCALE.body.max, fontWeight: 600, color: YELLOW,
          fontFamily: FONT_STACK, fontStyle: "italic",
        }}>
          Se ele tá só na tua cabeça, ele não existe.
        </div>

        <div style={{
          opacity: docP,
          fontSize: FONT_SCALE.h2.min, fontWeight: 800, color: GREEN,
          fontFamily: FONT_STACK, letterSpacing: -1,
        }}>
          Documenta hoje.
        </div>

        {/* Profile Instagram CTA */}
        <div style={{
          opacity: profileP,
          transform: `translateY(${interpolate(profileP, [0, 1], [15, 0], CLAMP)}px)`,
          display: "flex", alignItems: "center", gap: 14,
          marginTop: 8,
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: "50%", overflow: "hidden",
            border: `3px solid ${GREEN}`,
            boxShadow: `0 0 20px ${GREEN}30`,
          }}>
            <Img
              src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
              style={{width: "100%", height: "100%", objectFit: "cover"}}
            />
          </div>
          <div>
            <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>@guilhermeribeiro.ai</div>
            <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>Café com teu ROI</div>
          </div>
        </div>

        <div style={{
          opacity: closeP,
          fontSize: FONT_SCALE.body.min, fontWeight: 500,
          color: MUTED, fontFamily: FONT_STACK, fontStyle: "italic",
        }}>
          Até amanhã.
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────
export const CafeEp13: React.FC = () => {
  // Build timeline with transitions between scenes
  // Scene N starts at: sum of previous scenes + transitions
  const timeline: Array<{from: number; dur: number}> = [];
  let offset = 0;
  for (let i = 0; i < SCENES.length; i++) {
    timeline.push({from: offset, dur: SCENES[i].dur});
    offset += SCENES[i].dur;
    if (i < SCENES.length - 1) {
      offset += TRANSITION_DUR; // Gap for transition
    }
  }

  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      {/* Background ambient layers */}
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(ellipse at 50% 30%, ${BLUE}18, #000 70%)`,
      }} />
      <Noise opacity={0.02} />
      <FilmGrain opacity={0.025} />
      <Vignette intensity={0.5} />

      {/* BGM with fade in/out */}
      <Audio
        src={staticFile("music/cafe-lofi.mp3")}
        volume={(frame) => {
          const total = CAFE_EP13_TOTAL;
          const fadeIn = interpolate(frame, [0, 45], [0, BGM_VOLUME], {extrapolateRight: "clamp"});
          const fadeOut = interpolate(frame, [total - 60, total], [BGM_VOLUME, 0], {extrapolateLeft: "clamp", extrapolateRight: "clamp"});
          return Math.min(fadeIn, fadeOut);
        }}
        loop
      />

      {/* Scenes with SceneFade transitions */}
      {SCENES.map(({dur, bg}, i) => {
        const sequenceStart = timeline[i].from;
        return (
          <Sequence key={i} from={sequenceStart} durationInFrames={dur}>
            <SceneFade duration={dur}>
              {[S1, S2, S3, S4, S5][i]()}
            </SceneFade>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

CafeEp13.displayName = "CafeEp13";
;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
